import { AttributesMixin, define, Model, type } from '@type-r/models';
import { createdUpdatedAttrs } from 'api/common';
import { root } from '../src/app/pages/inmatch/api/common';
import { fetchJson, restfulIO } from 'api/endpoint';

@define
export class BaseActivityData extends Model {
    static attributes = {
        type: ""
    }

    // Don't use AttributesMixin in the base class; it adds "$" property, and when it's added in a sublass it breaks the TS typechecker.
    // As a workaround, declare attribute types manually. There's no good solution to that yet, but subclasses are rather rare, so...
    type : string

    static create<M extends new (...args: any[]) => any>(this: M, a?: any, b?: any): InstanceType<M>;
    static create( attributes, options ) : BaseActivityData {
        if(!attributes) return null;
        switch(attributes.type ) {
            case "RemittanceAdviceReceived" : return new IncomingRemitanceAdviceActivity(attributes, options)
            case "RALinkedToInvoices" : return new RemittanceLinkedToInvoiceActivity(attributes, options)
            case "RATransactionReconciled" : return new RemittanceLinkedToTransactionReconciledActivity(attributes, options);
            case "IncomingARTransaction" :
            case "IncomingNonARTransaction" : return new IncomingTransactionActivity(attributes, options);
            case "TransactionMovedToAR" : return new TransactionMovedToARActivity(attributes, options);
            case "TransactionMovedToNonAR" : return new TransactionMovedToNonARActivity(attributes, options);
            case "TransactionReconciled" :
            case "TransactionFullyReconciled" : return new TransactionReconciledActivity(attributes, options);
            case "TransactionUnmatched" : return new TransactionUnmatchedActivity(attributes, options);
            case "TransactionMovedUAC" :
            case "TransactionMovedFromUAC" : return new TransactionMovedUACActivity(attributes, options);
            case "PaymentSentToPTS":
            case "PaymentUploadFile" :
                 return new DescriptionOnlyActivity(attributes, options);
        }
    };
}

const SanitizedDate = type(Date).parse(date => {
    const withZone = date && (date.endsWith("z") || date.endsWith("Z") || date.endsWith("+00:00"));
    return date && withZone ? new Date(date) : new Date(date + 'Z')
});

export interface IncomingRemitanceAdviceActivity extends AttributesMixin<typeof IncomingRemitanceAdviceActivity>{}
@define
export class IncomingRemitanceAdviceActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "RemittanceAdviceReceived",
        remittanceDetails: {
            amount : Number,
            statementDate : SanitizedDate,
            valueDate : SanitizedDate,
            remittanceDocument : String
        },
        relationshipDetails : {
            customerName : String,
            customerId : String,
            supplierLegalEntityName : String,
            supplierCompanyCode : String,
        },
    }
}

export interface RemittanceLinkedToTransactionReconciledActivity extends AttributesMixin<typeof RemittanceLinkedToTransactionReconciledActivity>{}

@define
export class RemittanceLinkedToTransactionReconciledActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "RATransactionReconciled",
        transactionDetails: {
            amount : Number,
            statementDate : SanitizedDate,
            valueDate : SanitizedDate,
            baiCode : String,
            transactionId : String
        },
        reconciliationDetails: {
            totalAmountApplied : Number,
            remainingAmount : Number,
            processingFee : Number,
            fxGainLoss : Number,
            discount : Number,
            writeOff : Number,
        },
        invoices: [{
            amount : Number,
            invoiceNumber : String,
            invoiceId : String,
        }],
        remittanceAdviceDetails : {
            amount : Number,
            document : String
        },
        relationshipDetails : {
            customerName : String,
            customerId : String,
            supplierLegalEntityName : String,
            supplierCompanyCode : String,
        },
    }
}

export interface RemittanceLinkedToInvoiceActivity extends AttributesMixin<typeof RemittanceLinkedToInvoiceActivity>{}

@define
export class RemittanceLinkedToInvoiceActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "RALinkedToInvoices",
        remittanceDetails: {
            amount : Number,
            statementDate : SanitizedDate,
            valueDate : SanitizedDate,
            remittanceDocument : String
        },
        relationshipDetails : {
            customerName : String,
            customerId : String,
            supplierLegalEntityName : String,
            supplierCompanyCode : String,
        },
        invoices: [{
            invoiceNumber : String,
            invoiceId : String
        }]
    }
}

const transactionBaseAttrs = {
    transactionDetails: {
        amount : Number,
        statementDate : SanitizedDate,
        valueDate : SanitizedDate,
        baiCode : String
    },
    relationshipDetails : {
        customerName : String,
        customerId : String,
        supplierLegalEntityName : String,
        supplierCompanyCode : String,
    },
};

export interface IncomingTransactionActivity extends AttributesMixin<typeof IncomingTransactionActivity>{}
@define
export class IncomingTransactionActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "IncomingTransaction",
        ...transactionBaseAttrs
    }
}

export interface TransactionMovedToARActivity extends AttributesMixin<typeof TransactionMovedToARActivity>{}
@define
export class TransactionMovedToARActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "TransactionMovedToAR",
        ...transactionBaseAttrs,
        additional : {
            comments : String,
            attachments : String
        }
    }
}

export interface TransactionMovedToNonARActivity extends AttributesMixin<typeof TransactionMovedToNonARActivity>{}
@define
export class TransactionMovedToNonARActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "TransactionMovedToNonAR",
        ...transactionBaseAttrs,
        additional : {
            comments : String,
            attachments : String
        }
    }
}

export interface TransactionReconciledActivity extends AttributesMixin<typeof TransactionReconciledActivity>{}
@define
export class TransactionReconciledActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "TransactionReconciled",
        ...transactionBaseAttrs,
        isFullyReconciled : Boolean,
        reconciliationDetails: {
            totalAmountApplied : Number,
            remainingAmount : Number,
            processingFee : Number,
            fxGainLoss : Number,
            discount : Number,
            writeOff : Number,
        },
        invoices: [{
            amount : Number,
            invoiceNumber : String,
            invoiceId : String,
        }],
        remittanceAdviceDetails : {
            remittanceId : Number,
            amount : Number,
            document : String,
        },
        additional : {
            comments : String,
            attachments : String
        }
    }
}

export interface TransactionUnmatchedActivity extends AttributesMixin<typeof TransactionUnmatchedActivity>{}
@define
export class TransactionUnmatchedActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "TransactionUnmatched",
        ...transactionBaseAttrs,
        unreconciledDetails: {
            unmatchedAmount : Number,
        },
        additional : {
            comments : String,
            attachments : String
        }
    }
}

export interface TransactionMovedUACActivity extends AttributesMixin<typeof TransactionMovedUACActivity>{}
@define
export class TransactionMovedUACActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "TransactionMovedUAC",
        movedToUAC : Boolean,
        transactionDetails: {
            amount : Number,
            unreconciledAmount : Number,
            statementDate : SanitizedDate,
            valueDate : SanitizedDate,
            baiCode : String
        },
        relationshipDetails : {
            customerName : String,
            customerId : String,
            supplierLegalEntityName : String,
            supplierCompanyCode : String,
        },
        additional : {
            comments : String,
            attachments : String
        }
    }
}

export interface Activity extends AttributesMixin<typeof Activity>{}
@define
export class Activity extends Model {
    static endpoint = restfulIO(root + 'activity'); // change this to go directly to inblock-activity-web service

    static attributes = {
        ...createdUpdatedAttrs,
        assetId : String,
        assetType: String,
        relationshipId : Number,
        type : String,
        description : String,
        comment : String,
        attachmentId : Number,
        data : BaseActivityData,
        userInfo : type({
            firstName : String,
            lastName : String,
            email : String,
            companyShortName : String
        }).dontSave
    }

    static async saveActivities(activities : Activity[], assetType: String) {
        const url = `/api/v1/activity/${assetType}`;
        return await fetchJson('POST', url, activities);
      }

      static async getActivityTypes() {
        const url = `/api/v1/activity/types`;
        return await fetchJson('GET', url,);
      }
}

export interface DescriptionOnlyActivity extends AttributesMixin<typeof DescriptionOnlyActivity>{}
@define
export class DescriptionOnlyActivity extends BaseActivityData {
    static attributes = {
        ...BaseActivityData.attributes,
        type: "DescriptionOnlyActivity",
    }
}